import { MouseEvent, ReactNode, SyntheticEvent, useEffect, useRef } from 'react';
import { strings } from '@/i18n/strings';
import styles from './Dialog.module.scss';

interface DialogProps {
	title: string;
	onClose: () => void;
	children: ReactNode;
}

export const Dialog = ({ title, onClose, children }: DialogProps) => {
	const ref = useRef<HTMLDialogElement>(null);

	useEffect(() => {
		const dialog = ref.current;
		dialog?.showModal();
		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		return () => {
			if (dialog?.open) dialog.close();
			document.body.style.overflow = previousOverflow;
		};
	}, []);

	const handleClick = (event: MouseEvent<HTMLDialogElement>) => {
		if (event.target === ref.current) onClose();
	};

	const handleCancel = (event: SyntheticEvent<HTMLDialogElement>) => {
		event.preventDefault();
		onClose();
	};

	return (
		<dialog ref={ref} className={styles.dialog} aria-label={title} onClick={handleClick} onCancel={handleCancel}>
			<span className={styles.handle} aria-hidden="true" />
			<header className={styles.header}>
				<h2 className={styles.title}>{title}</h2>
				<button
					type="button"
					className={styles.close}
					aria-label={strings.dialog.closeAriaLabel}
					onClick={onClose}
				>
					<svg
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth={1.5}
						strokeLinecap="round"
						strokeLinejoin="round"
						aria-hidden="true"
					>
						<path d="M18 6 6 18M6 6l12 12" />
					</svg>
				</button>
			</header>
			<div className={styles.body}>{children}</div>
		</dialog>
	);
};
